import React from "react"
import axios from "axios"
import { delay } from "../utils/delay"
import { pathOr, pipe } from "ramda";
import { setApiError, setRemainingCredits } from "../components/dashboard/actions";
import { redactParams } from "../utils/app";

const ASYNC_JOB_CHECK_DELAY = 1000;

const REDACT_PARAMETERS = ['X-USER-TOKEN', 'X-API-KEY', 'x-api-key', 'x-user-token'];

let store;

export const injectStore = (_store) => {
  store = _store;
};

const parseData = (data) => {
  try {
    return JSON.stringify(JSON.parse(data), null, 4);
  } catch (e) {
    return data;
  }
};

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_DOCPARSER_TEMPLATE_EDITOR_PDFCO_API_URL,
});

const fetchApiKey = async () => {
  const response = await axios.get(
    `${process.env.REACT_APP_PYTHON_API_URL}/user/fetch-api-key`,
    {
      headers: {
        Authorization: `Bearer ${getCookie("token")}`,
      },
    }
  );

  return {
    apiKey: response.data.data,
  };
};

const attachIntegrationKeyRequestInterceptor = config => {
  const makeHeaders = () => {
    return {
      'X-INTEGRATION-KEY': process.env.REACT_APP_API_INTEGRATION_KEY,
    };
  };

  const headers = makeHeaders();

  return {
    ...config,
    headers: {
      ...config.headers,
      ...headers,
    },
  };
};


const attachApiKeyRequestInterceptor = (() => {
  const apiKey = {
    value: null,
    promisify: null,
  };

  const makeAttach = async () => {
    if (!apiKey.value) {
      if (!apiKey.promisify) {
        const promise = new Promise((resolve, reject) => {
          fetchApiKey()
            .then(({ apiKey }) => resolve(apiKey))
            .catch(reject);
        });

        apiKey.promisify = promise
          .then((key) => {
            apiKey.value = key;
          })
          .finally(() => {
            apiKey.promisify = null;
          });
      }

      await apiKey.promisify;
    }
  };

  return async (config) => {
    const makeHeaders = (customConfig) => {
      const makeAuth = () => {
        return {
          "x-api-key": apiKey.value,
        };
      };

      return {
        ...makeAuth(),
      };
    };

    await makeAttach();

    const headers = makeHeaders();

    return {
      ...config,
      headers: {
        ...config.headers,
        ...headers,
      },
    };
  };
})();

apiClient.interceptors.request.use(
  pipe(attachIntegrationKeyRequestInterceptor, attachApiKeyRequestInterceptor),
  (error) => Promise.reject(error)
);

const safeParseRequestData = requestData => {
  try {
    return JSON.parse(requestData)
  } catch(error) {
    return requestData
  }
}

const shouldCheckBackgroundJob = requestData => {
  const data = safeParseRequestData(requestData);

  if (!data) {
    return false;
  }

  if (data instanceof FormData) {
    return !!requestData.get('async');
  }

  return !!data.async;
}


apiClient.interceptors.response.use(response => {
  const remainingCredits = pathOr(
    null,
    ["data", "remainingCredits"],
    response
  );
  if (remainingCredits) {
    store.dispatch(setRemainingCredits(remainingCredits));
  }

  // CONSOLE LOG
  console.groupCollapsed(
    response.config.method.toUpperCase() +
    " " +
    pathOr("", ["config", "url"], response)
  );
  console.groupCollapsed("HEADERS");
  console.dir(
    JSON.stringify(redactParams(pathOr({}, ["config", "headers"], response), REDACT_PARAMETERS), null, 4)
  );
  console.groupEnd();

  console.groupCollapsed("BODY");
  console.dir(parseData(pathOr("", ["config", "data"], response)));
  console.groupEnd();

  console.log(`RESPONSE HTTP CODE: ${pathOr("", ["status"], response)}`);

  console.groupCollapsed("RESPONSE BODY");
  console.dir(JSON.stringify(pathOr({}, ["data"], response), null, 4));
  console.groupEnd();

  console.groupEnd();

  if (shouldCheckBackgroundJob(response.config.data)) {
    return checkBackgroundJob(response.data.jobId, response)
  }

  return response;
}, (error) => {
  const errorData = pathOr("XXX", ["response", "data"], error);
  store.dispatch(setApiError(errorData));
  // CONSOLE LOG
  console.log("REQUEST ERROR: ", error);

  return Promise.reject(error);
});

function getCookie(name) {
  let matches = document.cookie.match(new RegExp(
    "(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + "=([^;]*)"
  ));
  return matches ? decodeURIComponent(matches[1]) : undefined
}

function checkBackgroundJob(jobId, originalResponse) {
  return delay(ASYNC_JOB_CHECK_DELAY).then(() => {
    return apiClient.post("/job/check", { jobId }).then((response) => {
      if (response.data.status !== "working") {
        return originalResponse;
      }

      return checkBackgroundJob(jobId, originalResponse);
    });
  });
}

export default apiClient
